// React
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

// API
import { retrieveEmployeeListOrgChart } from "@Miscellaneous/api/organizationChart";

// Components
import PageLayer from "@Layouts/views/PageLayer";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import OrganizationChart from "@Miscellaneous/components/OrganizationChart";

// Assets
import empty from "@/assets/images/commons/empty.png";
import { ReactComponent as Icon } from "@/assets/icons/header/ellipsis/OrganizationChartIcon.svg";

// MUI
import { Stack, SvgIcon } from "@mui/material";

// Types
import { ILocation } from "@/Common/interfaces/location";
import { StringMap } from "@/Common/interfaces/types";
import { EmployeeNode } from "@/Miscellaneous/interfaces/orgchart";

const placeholderData: EmployeeNode = {
	payrollSourceId: {
		idValue: "1",
		idField: "",
		displayId: "",
		companyId: "",
		providerName: "",
	},
	supervisorId: "",
	loading: true,
	children: [
		{
			payrollSourceId: {
				idValue: "1.1",
				idField: "",
				displayId: "",
				companyId: "",
				providerName: "",
			},
			children: [
				{
					payrollSourceId: {
						idValue: "1.1.1",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.1",
				},
				{
					payrollSourceId: {
						idValue: "1.1.2",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.1",
				},
				{
					payrollSourceId: {
						idValue: "1.1.3",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.1",
				},
			],
			supervisorId: "1",
		},
		{
			payrollSourceId: {
				idValue: ".2",
				idField: "",
				displayId: "",
				companyId: "",
				providerName: "",
			},
			children: [
				{
					payrollSourceId: {
						idValue: "1.2.1",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.2",
				},
				{
					payrollSourceId: {
						idValue: "1.2.2",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.2",
				},
				{
					payrollSourceId: {
						idValue: "1.2.3",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.2",
				},
			],
			supervisorId: "1",
		},
		{
			payrollSourceId: {
				idValue: "1.3",
				idField: "",
				displayId: "",
				companyId: "",
				providerName: "",
			},
			children: [
				{
					payrollSourceId: {
						idValue: "1.3.1",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.3",
				},
				{
					payrollSourceId: {
						idValue: "1.3.2",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.3",
				},
				{
					payrollSourceId: {
						idValue: "1.3.3",
						idField: "",
						displayId: "",
						companyId: "",
						providerName: "",
					},
					supervisorId: "1.3",
				},
			],
			supervisorId: "1",
		},
	],
};

export default function OrganizationChartView() {
	const location = useSelector<{ location: { location: ILocation } }, ILocation>((state) => state.location.location);
	const locationId = location?.locationId;

	const { data } = useQuery<EmployeeNode>({
		queryKey: ["usersByStatusOrgchart", locationId],
		queryFn: async () => {
			const res = await retrieveEmployeeListOrgChart(locationId, { withProfilePicture: true });

			if (!res.length) {
				return null;
			}

			let rootNode: EmployeeNode = {} as EmployeeNode;
			const checkForRoot = res?.filter((user: EmployeeNode) => !user?.supervisorId);

			if (checkForRoot?.length > 1) {
				checkForRoot?.forEach((user: EmployeeNode) => {
					if (user) {
						user.supervisorId = "main-root";
					}
				});
				res.unshift({
					payrollSourceId: {
						idValue: "main-root",
					},
					root: true,
					supervisorId: null,
				});
			}

			const idMapping = res.reduce((acc: StringMap<string>, el: EmployeeNode, i: string) => {
				if (el?.payrollSourceId?.idValue) {
					acc[el?.payrollSourceId?.idValue] = i;
				}
				return acc;
			}, {});

			res.forEach((el: EmployeeNode) => {
				if (!el?.supervisorId) {
					rootNode = el;
					return;
				}
				const parentEl = res[idMapping[el.supervisorId]];
				if (parentEl) {
					parentEl.children = [...(parentEl?.children ?? []), el];
				}
			});

			rootNode = {
				children: [rootNode],
				payrollSourceId: {
					idValue: "hidden",
					idField: "",
					displayId: "",
					companyId: "",
					providerName: "",
				},
				supervisorId: "hidden-root",
			};

			return rootNode;
		},
		enabled: Boolean(locationId),
		staleTime: Infinity,
		placeholderData,
	});

	return (
		<PageLayer
			title="Organization Chart"
			icon={
				<Stack width={1} direction={"row"} justifyContent={"center"} alignItems={"center"}>
					<SvgIcon viewBox="0 -3 24 24" className="text-[var(--light-blue-color)] !text-[27px]" component={Icon} />
				</Stack>
			}
		>
			{data ? <OrganizationChart data={data} /> : <EmptyBody image={empty} title={"No Users found"} bodyHeight={"590px"} />}
		</PageLayer>
	);
}
